/**
 * IndexedDB wrapper for HydraDatacentre client-side storage.
 *
 * Used for:
 * - persisted read cache (localCache)
 * - sync queue (SyncEngine)
 * - sync metadata (last version, status)
 * - current base info
 *
 * SSoT remains the Go server. This is local cache/queue only.
 */

const DB_NAME = 'hydra-client';
const DB_VERSION = 1;
const STORE_NAME = 'kv';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      reject(new Error('IndexedDB not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });

  return dbPromise;
}

export async function idbGet<T>(key: string): Promise<T | undefined> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result as T | undefined);
      };
      request.onerror = () => {
        reject(request.error ?? new Error(`idbGet failed: ${key}`));
      };
    });
  } catch (err) {
    // IndexedDB unavailable in test/server environments
    return undefined;
  }
}

export async function idbSet<T>(key: string, value: T): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(value, key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error ?? new Error(`idbSet failed: ${key}`));
    });
  } catch (err) {
    // Fail silently if IndexedDB not available; caller should handle in-memory fallback
  }
}

export async function idbDelete(key: string): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error ?? new Error(`idbDelete failed: ${key}`));
    });
  } catch (err) {
    // ignore
  }
}

export async function idbKeys(): Promise<string[]> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAllKeys();

      request.onsuccess = () => resolve(request.result as string[]);
      request.onerror = () => reject(request.error ?? new Error('idbKeys failed'));
    });
  } catch (err) {
    return [];
  }
}

export async function idbClear(): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error ?? new Error('idbClear failed'));
    });
  } catch (err) {
    // ignore
  }
}

export async function idbKeysByPrefix(prefix: string): Promise<string[]> {
  const keys = await idbKeys();
  return keys.filter(k => k.startsWith(prefix));
}

export async function idbGetByPrefix<T>(prefix: string): Promise<Record<string, T>> {
  const keys = await idbKeysByPrefix(prefix);
  const result: Record<string, T> = {};
  for (const key of keys) {
    const value = await idbGet<T>(key);
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}
